import React, { useState } from "react";
import { FaTrash } from "react-icons/fa";
import { IconWrapper } from "../../components/IconWrapper";
import { useColorMode } from "@chakra-ui/react";

interface NotificationItemProps {
  notification: {
    message: string;
    timestamp: Date;
    read: boolean;
    projectName?: string;
    avatar?: string;
  };
  index: number;
  markAsRead: (index: number) => void;
  deleteNotification: (index: number) => void;
  color: string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  index,
  markAsRead,
  deleteNotification,
  color,
}) => {
  const [isAnimatingCheck, setIsAnimatingCheck] = useState(false);
  const formattedTime = notification.timestamp
    ? notification.timestamp.toLocaleString()
    : "";

  // Function to generate a simple avatar with initials
  const generateAvatar = (name: string | undefined) => {
    const initials = name ? name.substring(0, 2).toUpperCase() : "?";
    // Simple color hash based on the first initial (can be more sophisticated)
    const colorHash = initials.charCodeAt(0) % 5;
    const colors = [
      "bg-red-400",
      "bg-blue-400",
      "bg-green-400",
      "bg-yellow-400",
      "bg-indigo-400",
    ];
    const bgColor = colors[colorHash];

    return (
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${bgColor}`}
      >
        {initials}
      </div>
    );
  };

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  return (
    <div
      className={`group relative p-3 flex items-center justify-between transition-all duration-200 ease-in-out ${
        isDark ? 'border-b border-[#2E2F33] hover:bg-[#14161B] bg-[#1A1C1F]' : 'border-b border-gray-200 hover:bg-gray-100 bg-white'
      }`}
    >
      <div className="flex items-center flex-1 min-w-0">
        {/* Avatar based on name */}
        <div className="mr-3">{generateAvatar(notification.projectName)}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {notification.projectName && (
              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${isDark ? 'text-white bg-[#2A3441]' : 'text-[#B59143] bg-[#FFF7E6] border border-[#E8C16C]'}`}>
                {notification.projectName}
              </span>
            )}
            <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>{formattedTime}</span>
          </div>
          <p className={`text-sm ${color} truncate`}>{notification.message}</p>
        </div>
      </div>

      <div className="flex items-center gap-1 ml-4">
        {/* Mark as Read Button */}
        {!notification.read && (
          <button
            onClick={() => {
              if (isAnimatingCheck) return;
              setIsAnimatingCheck(true);
              // Immediately update read state so it moves to Read section
              markAsRead(index);
            }}
            className={`relative p-1 rounded-full transition-all duration-300 focus:outline-none ${isDark ? 'text-[#FBD282] focus:ring-2 focus:ring-[#B59143]' : 'text-[#B59143] focus:ring-2 focus:ring-[#B59143]/40'} hover:opacity-90`}
            aria-label="Mark as read"
            title="Mark as read"
          >
            {/* Sleek gradient check-in-circle */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className={`w-5 h-5 relative z-10 transition-transform duration-200 ${
                isAnimatingCheck ? "scale-95" : "group-hover:scale-110"
              }`}
            >
              <defs>
                <linearGradient id="tickGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FBD282" />
                  <stop offset="100%" stopColor="#B59143" />
                </linearGradient>
              </defs>
              <circle cx="12" cy="12" r="10" fill="url(#tickGrad)" opacity="0.15" />
              <path
                d="M7 12.5l3 3L17 8.8"
                fill="none"
                stroke="#B59143"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {/* Delete Button */}
        <button
          onClick={() => deleteNotification(index)}
          className={`p-1 transition-colors duration-200 rounded-full ${isDark ? 'text-[#FBD282] hover:bg-[#2A3441]' : 'text-[#B59143] hover:bg-[#FFF7E6]'}`}
          aria-label="Delete notification"
        >
      <IconWrapper icon={FaTrash} size={14} className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;
